import React, { ReactNode, useCallback } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import _ from "lodash";

import theme from "theme";

import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

interface IBaseSettings<T> {
  label: string;
  options: Array<{
    label: ReactNode;
    value: T;
    disabled?: boolean;
    tooltip?: string;
  }>;
  onChange: (value: T, checked: boolean) => void;
  dataTestid?: string;
}

interface IMultipleValue<T> extends IBaseSettings<T> {
  multiple: true;
  value: Array<T>;
}

interface ISingleValue<T> extends IBaseSettings<T> {
  multiple?: false;
  value: T;
}

type ISettingsCategory<T> = IMultipleValue<T> | ISingleValue<T>;

const SettingsCategory = <T,>(props: ISettingsCategory<T>) => {
  const { label, options, onChange, dataTestid } = props;
  const handleRadioClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value as T, true);
    },
    [onChange]
  );

  const handleCheckboxClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onChange(event.target.value as T, checked);
    },
    [onChange]
  );

  return (
    <Stack
      alignItems="flex-start"
      data-testid={`${dataTestid}-settings-section`}
      sx={{ flexBasis: "50%" }}
    >
      <Text variant="text2" fontWeight="700" data-testid="settings-label">
        {label}
      </Text>
      <FormControl>
        <RadioGroup
          value={props.value}
          onChange={handleRadioClick}
          sx={{ margin: theme.spacing(1) }}
          data-testid="settings-options"
        >
          {options.map(({ disabled, label, value, tooltip }, index) => {
            const item = (
              <FormControlLabel
                key={index}
                value={value}
                control={
                  props.multiple ? (
                    <Checkbox
                      disabled={disabled}
                      checked={props.value.includes(value)}
                      sx={{
                        padding: 0,
                        marginRight: 1,
                        alignSelf: "flex-start",
                      }}
                      onChange={handleCheckboxClick}
                      data-testid="checkbox-button"
                    />
                  ) : (
                    <Radio data-testid="radio-button" />
                  )
                }
                label={label}
                data-testid="setting-option"
              />
            );

            if (!_.isNil(tooltip)) {
              return (
                <Tooltip title={tooltip}>
                  <span>{item}</span>
                </Tooltip>
              );
            }

            return item;
          })}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};

export default SettingsCategory;
